<template>
    <router-link
        :to="isDeleting ? '#' : `/galleries/${gallery.slug}/settings`"
        class="group relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 ring-1 ring-gray-900/5 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
    >
        <img
            v-if="gallery.small_cover_url"
            :src="gallery.small_cover_url"
            :alt="gallery.name"
            class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div v-else class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-100">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.2">
                <path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5M21 3.75H3A.75.75 0 0 0 2.25 4.5v15" />
            </svg>
            <span class="text-xs text-gray-400">{{ $t('gallery_item.no_cover') }}</span>
        </div>
        <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>

        <!-- Pin button (top left) -->
        <div class="absolute top-3 left-3 z-10">
            <button
                v-if="!isDeleting"
                @click.prevent="gallery.is_pinned ? unpinGallery(gallery.slug) : pinGallery(gallery.slug)"
                :class="gallery.is_pinned
                    ? 'text-white bg-transparent border border-transparent group-hover:bg-white/20 group-hover:backdrop-blur-md group-hover:border-white/10 group-hover:shadow-sm hover:!bg-white/40 opacity-100'
                    : 'text-white bg-transparent border border-transparent opacity-0 group-hover:opacity-100 group-hover:bg-white/20 group-hover:backdrop-blur-md group-hover:border-white/10 group-hover:shadow-sm hover:!bg-white/40'"
                class="flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200"
            >
                <svg class="w-4 h-4 shrink-0" viewBox="0 0 18 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.27851 7.18016C3.72241 7.25256 3.44436 7.28876 3.38422 7.46551C3.32408 7.64226 3.51876 7.83694 3.90812 8.2263L9.64966 13.9678C10.0836 14.4018 10.3006 14.6188 10.4829 14.5481C10.6653 14.4774 10.6795 14.1648 10.7081 13.5396C10.8811 9.75464 11.915 8.1738 14.5139 7.06964C14.918 6.89797 15.12 6.81213 15.1504 6.65558C15.1808 6.49902 15.0312 6.34937 14.7319 6.05008L11.4597 2.77784C11.1238 2.44201 10.9559 2.2741 10.7919 2.31418C10.6279 2.35426 10.5531 2.5912 10.4034 3.06507C9.59426 5.62654 8.03961 6.69049 4.27851 7.18016Z"/>
                    <path d="M2.64645 14.6464C2.45118 14.8417 2.45118 15.1583 2.64645 15.3536C2.84171 15.5488 3.15829 15.5488 3.35355 15.3536L2.64645 14.6464ZM7 11L6.64645 10.6464L2.64645 14.6464L3 15L3.35355 15.3536L7.35355 11.3536L7 11Z"/>
                </svg>
            </button>
        </div>

        <!-- Delete / Restore button (top right) -->
        <div class="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
            <button
                v-if="isDeleting"
                @click.prevent="restoreGallery(gallery.slug)"
                class="flex items-center justify-center px-2.5 h-8 bg-blue-500/80 hover:bg-blue-500 backdrop-blur-md rounded-lg text-white text-xs font-medium border border-white/10 shadow-sm"
            >
                {{ $t('gallery_item.restore') }}
            </button>
            <button
                @click.prevent="deleteGallery(gallery.slug)"
                class="flex items-center justify-center w-8 h-8 bg-white/20 hover:bg-red-500/90 backdrop-blur-md rounded-lg text-white border border-white/10 shadow-sm transition-colors"
                :title="$t('gallery_item.delete')"
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
            </button>
        </div>

        <!-- Bottom info -->
        <div class="absolute bottom-0 left-0 right-0 p-4 text-white">
            <h3 class="text-lg font-semibold tracking-tight mb-1 line-clamp-2">{{ gallery.name }}</h3>
            <div class="flex items-center text-xs text-gray-300 min-h-[24px] w-full flex-wrap gap-2.5">
                <span v-if="gallery.date" class="truncate">{{ gallery.date }}</span>

                <div class="flex shrink-0 items-center gap-1.5">
                    <ExclamationTriangleIcon v-if="gallery.is_expired_storage" class="size-3.5 text-red-400" />
                    <div class="flex items-center gap-1 bg-white/10 backdrop-blur-sm px-2 py-0.5 rounded-full border border-white/10">
                        <template v-if="gallery.storage_to_date">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                            </svg>
                            <span class="whitespace-nowrap">{{ gallery.storage_to_date }}</span>
                        </template>
                        <template v-else>
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M12 12c-2-2.5-4-4-6-4a4 4 0 0 0 0 8c2 0 4-1.5 6-4zm0 0c2 2.5 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.5-6 4z"/>
                            </svg>
                            <span class="whitespace-nowrap">{{ $t('gallery_item.storage_without_limit') }}</span>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </router-link>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ExclamationTriangleIcon } from "@heroicons/vue/20/solid";

export default defineComponent({
    name: 'GalleryCardGridItemComponent',
    components: { ExclamationTriangleIcon },

    props: {
        gallery: {
            type: Object,
            required: true
        },

        isDeleting: {
            type: Boolean,
            default: false
        }
    },

    methods: {
        deleteGallery(slug: string) {
            this.$emit('delete-gallery', slug);
        },

        restoreGallery(slug: string) {
            this.$emit('restore-gallery', slug);
        },

        pinGallery(slug: string) {
            this.$emit('pin-gallery', slug);
        },

        unpinGallery(slug: string) {
            this.$emit('unpin-gallery', slug);
        }
    }
});
</script>
